import { Palette } from "lucide-react";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { isSuperAdmin } from "@/lib/roles";
import { ChartSettingsProvider } from "@/components/portal/charts/settings/ChartSettingsProvider";
import { ChartSettingsAdminPanel } from "@/components/portal/spaces/widgets/reporting/config/styling/ChartSettingsAdminPanel";

/**
 * Chart settings cascade admin editor (echarts-charting-maturity-audit
 * M2-web-b). Follows `routes/portal/platform/theme.tsx`'s own convention —
 * ONE dedicated route, internal tabs for organisation vs platform scope,
 * gated by `isSuperAdmin` — rather than the plan's literal two-file
 * wording (a separate `.../settings/chart-settings` org tab). That literal
 * wording doesn't match how `settings.tsx`'s org/platform split is
 * actually implemented in this codebase (an internal Tabs split, not two
 * routes) — `theme.tsx` is the closer, already-shipped precedent for a
 * cascading (org + platform) settings surface, and `chart-settings.tsx`
 * IS the plan's named platform-admin file path. Documented here per plan
 * §7 ("a builder who believes one of these is wrong stops and writes the
 * objection... rather than silently reconciling by guessing") — this is a
 * UI-location choice, not a re-opening of any §3/§4 decision.
 *
 * §11.1 RBAC default: platform-admin only edits BOTH scopes for now
 * (organisation-owner self-serve is an explicit future toggle, not built
 * here) — `canEdit` is `isSuperAdmin(session)` for both tabs.
 *
 * Mounts `ChartSettingsProvider` for the selected organisation (no space)
 * (chart-settings-admin-cascade-followups D1), so the Organisation tab sees
 * the stored platform level, its locks and the registered palettes. A save
 * refetches `/resolved` through `useChartSettingsMutation`'s existing
 * invalidation.
 */
export const Route = createFileRoute("/portal/platform/chart-settings")({
  component: ChartSettingsAdminPage,
});

function ChartSettingsAdminPage() {
  const { session } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const isUserSuperAdmin = isSuperAdmin(session);
  const organisationId = selectedOrganisation?.organisationId;
  // Controlled (rather than Tabs' own defaultValue) so isActiveTab below can
  // tell ChartSettingsAdminPanel which of the two forceMount'd panels is the
  // one actually visible right now — see isActiveTab's own doc comment.
  const [activeTab, setActiveTab] = useState<"organisation" | "platform">("organisation");

  if (!isUserSuperAdmin) {
    return (
      <div className="container mx-auto py-6">
        <p className="text-sm text-muted-foreground">
          Chart settings are managed by a platform administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="container mx-auto space-y-6 py-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <Palette className="h-6 w-6" />
          Chart Settings
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Master defaults, palettes and locks for every chart across the platform.
        </p>
      </div>

      <ChartSettingsProvider organisationId={organisationId}>
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "organisation" | "platform")}>
          <TabsList>
            <TabsTrigger value="organisation">Organisation Defaults</TabsTrigger>
            <TabsTrigger value="platform">Platform Defaults</TabsTrigger>
          </TabsList>

          {/* #1029: forceMount keeps both panels MOUNTED across a tab switch
              (without it, Radix unmounts the inactive TabsContent entirely,
              and ChartSettingsAdminPanel's edit/dirty/lock state lives in
              local component state, so switching tabs and back silently
              discarded every unsaved edit with no prompt) — but forceMount
              does NOT also hide the inactive panel: this Radix version's
              Content sets `hidden={!(forceMount || isSelected)}`, so a
              literal `forceMount` makes `hidden` permanently false regardless
              of selection. `data-[state=inactive]:hidden` (Tailwind's
              data-attribute variant, keyed off Radix's own `data-state`) is
              what actually hides the inactive one — confirmed by DOM
              inspection during gate-review: without this class both panels
              rendered stacked and simultaneously visible on page load, a
              highly visible defect this fix's own test coverage would have
              missed had the review not screenshotted the live page rather
              than trusting the assertions alone.
              isActiveTab additionally tells the inactive panel to skip its
              7-ECharts-instance live preview strip (#1030 already made that
              strip cheap to rebuild on a keystroke; forceMount would otherwise
              keep it permanently mounted TWICE at all times, not just cheaply
              rebuilding). */}
          <TabsContent value="organisation" className="mt-6 data-[state=inactive]:hidden" forceMount>
            {organisationId ? (
              <ChartSettingsAdminPanel
                scope={{ level: "organisation", organisationId }}
                canEdit={isUserSuperAdmin}
                scopeLabel="Organisation"
                isActiveTab={activeTab === "organisation"}
              />
            ) : (
              <p className="text-sm text-muted-foreground">Select an organisation first.</p>
            )}
          </TabsContent>

          <TabsContent value="platform" className="mt-6 data-[state=inactive]:hidden" forceMount>
            <ChartSettingsAdminPanel
              scope={{ level: "platform" }}
              canEdit={isUserSuperAdmin}
              scopeLabel="Platform"
              isActiveTab={activeTab === "platform"}
            />
          </TabsContent>
        </Tabs>
      </ChartSettingsProvider>
    </div>
  );
}
